import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, API_BASE } from '../../api/client';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Chip from '@mui/material/Chip';
import AdminLoading from './AdminLoading';
import AdminEmptyState from './AdminEmptyState';
import { IoShirtOutline, IoAddOutline, IoImageOutline, IoSearchOutline, IoCloseOutline, IoFlashOutline } from 'react-icons/io5';

function normalizeGender(g) {
  const s = String(g ?? '').trim().toLowerCase();
  if (!s) return '';
  if (['f', 'female', 'woman', 'women', 'womens', 'ladies'].includes(s)) return 'Female';
  if (['m', 'male', 'man', 'men', 'mens', 'gents'].includes(s)) return 'Male';
  if (s === 'unisex') return 'Unisex';
  if (s === 'boys') return 'Boys';
  if (s === 'girls') return 'Girls';
  return String(g).trim();
}

export default function AdminProducts() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [syncImagesLoading, setSyncImagesLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  /** 'all' | 'uncategorized' | category id string */
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  /** 'all' | 'published' | 'draft' */
  const [statusFilter, setStatusFilter] = useState('all');
  const [quickEdit, setQuickEdit] = useState(null);
  const [qForm, setQForm] = useState({});
  const [qSaving, setQSaving] = useState(false);
  const [qLoading, setQLoading] = useState(false);
  const [qError, setQError] = useState('');
  const [colorOptions, setColorOptions] = useState([]);

  const filteredList = useMemo(() => {
    let result = list;
    // status filter
    if (statusFilter === 'published') result = result.filter((p) => Number(p.is_visible ?? 1) === 1);
    else if (statusFilter === 'draft') result = result.filter((p) => Number(p.is_visible ?? 1) === 0);
    // search filter
    const q = searchQuery.trim().toLowerCase();
    if (!q) return result;
    return result.filter((p) => {
      const code = String(p.code ?? '').toLowerCase();
      const name = String(p.name ?? '').toLowerCase();
      const idStr = String(p.id ?? '');
      const color = String(p.color ?? '').toLowerCase();
      const slug = String(p.slug ?? '').toLowerCase();
      const catName = String(
        categories.find((c) => String(c.id) === String(p.category_id))?.name ?? ''
      ).toLowerCase();
      return (
        code.includes(q) ||
        name.includes(q) ||
        idStr.includes(q) ||
        color.includes(q) ||
        slug.includes(q) ||
        catName.includes(q)
      );
    });
  }, [list, searchQuery, categories, statusFilter]);

  const paginatedList = useMemo(
    () => filteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredList, page, rowsPerPage]
  );

  const currentPageIds = useMemo(() => paginatedList.map((p) => p.id), [paginatedList]);
  const allCurrentSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedIds.has(id));
  const someCurrentSelected = currentPageIds.some((id) => selectedIds.has(id));

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const qs =
      categoryFilter === 'all' ? '' : `?category_id=${encodeURIComponent(categoryFilter)}`;
    return api(`/api/admin/products${qs}`)
      .then(setList)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [categoryFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api('/api/admin/products/category-options')
      .then(setCategories)
      .catch(() =>
        api('/api/admin/sync/categories')
          .then(setCategories)
          .catch(() => setCategories([]))
      );
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
    setPage(0);
  }, [categoryFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredList.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredList.length, rowsPerPage, page]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  useEffect(() => {
    fetch(`${API_BASE}/api/variations`)
      .then((r) => r.json())
      .then((d) => setColorOptions(Array.isArray(d.Color) ? d.Color : []))
      .catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await api(`/api/admin/products/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allCurrentSelected) {
        currentPageIds.forEach((id) => next.delete(id));
      } else {
        currentPageIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} product(s)? This cannot be undone.`)) return;
    try {
      const data = await api('/api/admin/bulk-delete-products', { method: 'POST', body: JSON.stringify({ ids: Array.from(selectedIds) }) });
      setSelectedIds(new Set());
      load();
      if (data.skipped > 0) setError(`${data.deleted} deleted. ${data.skipped} product(s) not deleted (they have orders).`);
      else setError('');
    } catch (err) {
      setError(err.message || 'Delete failed');
    }
  };

  const handleSyncImages = async () => {
    setSyncImagesLoading(true);
    setError('');
    setSuccessMessage('');
    try {
      const data = await api('/api/admin/products/sync-images', { method: 'POST' });
      load();
      if (data.imagesLinked > 0) {
        setSuccessMessage(`Synced: ${data.productsUpdated} product(s), ${data.imagesLinked} image(s) linked from uploads/product-images.`);
      } else {
        setSuccessMessage('No image files found in uploads/product-images (expected names: productId-index.ext, e.g. 172-0.png).');
      }
    } catch (err) {
      setError(err.message || 'Sync images failed');
    } finally {
      setSyncImagesLoading(false);
    }
  };

  const openQuickEdit = async (p) => {
    setQError('');
    setQForm({});
    setQuickEdit(p);
    setQLoading(true);
    try {
      const full = await api(`/api/admin/products/${p.id}`);
      const attrs = full.attributes && typeof full.attributes === 'object' ? full.attributes : {};
      const colorFromAttrs = Array.isArray(attrs.Color) && attrs.Color.length ? attrs.Color[0] : null;
      setQForm({
        name: full.name || '',
        code: full.code || '',
        color: full.color || colorFromAttrs || '',
        category_id: full.category_id ? String(full.category_id) : '',
        gender: normalizeGender(full.gender),
        is_visible: full.is_visible !== undefined && full.is_visible !== null ? Number(full.is_visible) : 1,
      });
    } catch (e) {
      setQError('Failed to load product: ' + (e.message || ''));
    } finally {
      setQLoading(false);
    }
  };

  const closeQuickEdit = () => { if (!qSaving) setQuickEdit(null); };

  const saveQuickEdit = async () => {
    setQSaving(true);
    setQError('');
    try {
      await api(`/api/admin/products/${quickEdit.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: qForm.name.trim() || undefined,
          code: qForm.code.trim() || undefined,
          color: qForm.color || null,
          category_id: qForm.category_id ? Number(qForm.category_id) : null,
          gender: qForm.gender || null,
          is_visible: qForm.is_visible,
        }),
      });
      setList((prev) =>
        prev.map((p) =>
          p.id === quickEdit.id
            ? {
                ...p,
                name: qForm.name.trim() || p.name,
                code: qForm.code.trim() || p.code,
                color: qForm.color || null,
                category_id: qForm.category_id ? Number(qForm.category_id) : null,
                gender: qForm.gender || null,
                is_visible: qForm.is_visible,
              }
            : p
        )
      );
      setQuickEdit(null);
    } catch (e) {
      setQError(e.message || 'Save failed');
    } finally {
      setQSaving(false);
    }
  };

  const patchListingFlag = async (productId, field, checked) => {
    setError('');
    try {
      await api(`/api/admin/products/${productId}/listing`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: checked ? 1 : 0 }),
      });
      setList((prev) =>
        prev.map((p) =>
          p.id === productId ? { ...p, [field]: checked ? 1 : 0 } : p
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update listing flags');
    }
  };

  if (loading) return <AdminLoading />;

  return (
    <Card>
      <CardHeader
        title="Product List"
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {selectedIds.size > 0 && (
              <Button variant="outlined" color="error" onClick={handleBulkDelete} sx={{ textTransform: 'none' }}>
                Delete selected ({selectedIds.size})
              </Button>
            )}
            {/* Sync images button hidden — disabled per admin request */}
            <Button variant="contained" startIcon={<IoAddOutline />} onClick={() => navigate('/admin/products/add')} sx={{ textTransform: 'none' }}>
              Add Product
            </Button>
          </Box>
        }
      />
      <CardContent>
        {error && <Typography color="error" className="admin-error" sx={{ mb: 2 }}>{error}</Typography>}
        {successMessage && <Typography color="primary" sx={{ mb: 2 }}>{successMessage}</Typography>}
        {/* Status filter tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            size="small"
            onChange={(_, v) => { if (v !== null) { setStatusFilter(v); setPage(0); } }}
            aria-label="Filter by status"
          >
            <ToggleButton value="all" sx={{ textTransform: 'none', px: 2 }}>
              All&nbsp;<Chip label={list.length} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
            </ToggleButton>
            <ToggleButton value="published" sx={{ textTransform: 'none', px: 2 }}>
              Published&nbsp;<Chip label={list.filter(p => Number(p.is_visible ?? 1) === 1).length} size="small" color="success" sx={{ height: 18, fontSize: '0.7rem' }} />
            </ToggleButton>
            <ToggleButton value="draft" sx={{ textTransform: 'none', px: 2 }}>
              Draft&nbsp;<Chip label={list.filter(p => Number(p.is_visible ?? 1) === 0).length} size="small" color="warning" sx={{ height: 18, fontSize: '0.7rem' }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search products"
            placeholder="Code, name, ID, color, category…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 260, flex: '1 1 240px', maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <IoSearchOutline size={20} />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'aria-label': 'Search products' }}
          />
          <TextField
            select
            size="small"
            label="Filter by category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ minWidth: 280 }}
            SelectProps={{
              displayEmpty: false,
              renderValue: (v) => {
                if (v === 'all') return 'All categories';
                if (v === 'uncategorized') return 'Uncategorized';
                const c = categories.find((x) => String(x.id) === String(v));
                return c ? c.name : `Category #${v}`;
              },
            }}
          >
            <MenuItem value="all">All categories</MenuItem>
            <MenuItem value="uncategorized">Uncategorized</MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        {list.length === 0 ? (
          <AdminEmptyState
            message={
              categoryFilter === 'all'
                ? 'No products yet. Add one to get started.'
                : 'No products in this category. Try another filter or clear the category.'
            }
            icon={IoShirtOutline}
          />
        ) : filteredList.length === 0 ? (
          <AdminEmptyState
            message="No products match your search. Try different keywords or clear the search box."
            icon={IoSearchOutline}
          />
        ) : (
          <>
          <TableContainer sx={{ maxHeight: 'min(70vh, 640px)', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someCurrentSelected && !allCurrentSelected}
                      checked={allCurrentSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all on this page"
                    />
                  </TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Also list under Shop → Festive vibes (header nav)">
                      <span>Festive</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Also list under Shop → Occasion (header nav)">
                      <span>Occasion</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedList.map((p) => (
                  <TableRow key={p.id} hover selected={selectedIds.has(p.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.has(p.id)}
                        onChange={() => toggleSelect(p.id)}
                        aria-label={`Select ${p.code}`}
                      />
                    </TableCell>
                    <TableCell>{p.id}</TableCell>
                    <TableCell>
                      {p.first_image_url ? (() => {
                        const imgSrc = p.first_image_url.startsWith('http') ? p.first_image_url : API_BASE + p.first_image_url;
                        return (
                          <Tooltip
                            title={
                              <Box
                                component="img"
                                src={imgSrc}
                                alt={p.name || ''}
                                sx={{ width: 220, height: 220, objectFit: 'contain', display: 'block', borderRadius: 1, bgcolor: '#fff' }}
                              />
                            }
                            placement="right"
                            arrow
                            componentsProps={{
                              tooltip: { sx: { bgcolor: '#fff', boxShadow: 4, p: 0.5, borderRadius: 2, maxWidth: 'none' } },
                              arrow: { sx: { color: '#fff' } },
                            }}
                            enterDelay={100}
                            leaveDelay={0}
                          >
                            <Box
                              component="img"
                              src={imgSrc}
                              alt=""
                              sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, cursor: 'zoom-in', display: 'block' }}
                            />
                          </Tooltip>
                        );
                      })() : (
                        <Box sx={{ width: 48, height: 48, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IoShirtOutline size={24} color="#999" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{p.code}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>
                      {Number(p.is_visible ?? 1) === 1
                        ? <Chip label="Published" size="small" color="success" variant="outlined" />
                        : <Chip label="Draft" size="small" color="warning" variant="outlined" />}
                    </TableCell>
                    <TableCell>{p.color || '–'}</TableCell>
                    <TableCell>{normalizeGender(p.gender) || '–'}</TableCell>
                    <TableCell>{categories.find((c) => c.id === p.category_id)?.name || '–'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Festive vibes shop filter" enterDelay={400}>
                        <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', justifyContent: 'center' }}>
                          <Switch
                            size="small"
                            checked={Boolean(Number(p.listing_festive_vibes))}
                            onChange={(_, c) => patchListingFlag(p.id, 'listing_festive_vibes', c)}
                            inputProps={{ 'aria-label': `Festive listing for ${String(p.code || p.id)}` }}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Occasion shop filter" enterDelay={400}>
                        <Box component="span" sx={{ display: 'inline-flex', verticalAlign: 'middle', justifyContent: 'center' }}>
                          <Switch
                            size="small"
                            checked={Boolean(Number(p.listing_occasion))}
                            onChange={(_, c) => patchListingFlag(p.id, 'listing_occasion', c)}
                            inputProps={{ 'aria-label': `Occasion listing for ${String(p.code || p.id)}` }}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" component="a" href={`/product/${p.id}`} target="_blank" rel="noopener noreferrer">View</Button>
                      <Button size="small" startIcon={<IoFlashOutline size={13} />} onClick={() => openQuickEdit(p)} sx={{ textTransform: 'none' }}>Quick Edit</Button>
                      <Button size="small" onClick={() => navigate(`/admin/products/edit/${p.id}`)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(p.id)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 25, 50, 100]}
            count={filteredList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Rows per page"
          />
          </>
        )}
      </CardContent>

      {/* ── Quick Edit Drawer ── */}
      <Drawer
        anchor="right"
        open={Boolean(quickEdit)}
        onClose={closeQuickEdit}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 400 }, p: 0 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <IoFlashOutline size={18} style={{ marginRight: 8, flexShrink: 0 }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
            Quick Edit — #{quickEdit?.id}
          </Typography>
          <IconButton size="small" onClick={closeQuickEdit} disabled={qSaving} aria-label="Close">
            <IoCloseOutline size={20} />
          </IconButton>
        </Box>

        {quickEdit && (
          <Box sx={{ p: 2.5, overflowY: 'auto', height: '100%' }}>
            {qLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">Loading product…</Typography>
              </Box>
            )}
            {/* Thumbnail */}
            {!qLoading && quickEdit.first_image_url && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Box
                  component="img"
                  src={quickEdit.first_image_url.startsWith('http') ? quickEdit.first_image_url : API_BASE + quickEdit.first_image_url}
                  alt=""
                  sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                />
              </Box>
            )}

            {!qLoading && <Stack spacing={2}>
              <TextField
                label="Product Name"
                size="small"
                fullWidth
                value={qForm.name}
                onChange={(e) => setQForm((f) => ({ ...f, name: e.target.value }))}
              />

              <TextField
                label="Code / SKU"
                size="small"
                fullWidth
                value={qForm.code}
                onChange={(e) => setQForm((f) => ({ ...f, code: e.target.value }))}
              />

              <FormControl size="small" fullWidth>
                <InputLabel>Color</InputLabel>
                <Select
                  label="Color"
                  value={qForm.color}
                  onChange={(e) => setQForm((f) => ({ ...f, color: e.target.value }))}
                >
                  <MenuItem value=""><em>— None —</em></MenuItem>
                  {colorOptions.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                  {/* Keep the existing value visible even if not in list */}
                  {qForm.color && !colorOptions.includes(qForm.color) && (
                    <MenuItem value={qForm.color}>{qForm.color}</MenuItem>
                  )}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={qForm.category_id}
                  onChange={(e) => setQForm((f) => ({ ...f, category_id: e.target.value }))}
                >
                  <MenuItem value=""><em>— Uncategorized —</em></MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  label="Gender"
                  value={qForm.gender}
                  onChange={(e) => setQForm((f) => ({ ...f, gender: e.target.value }))}
                >
                  <MenuItem value=""><em>— Not set —</em></MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Unisex">Unisex</MenuItem>
                  <MenuItem value="Boys">Boys</MenuItem>
                  <MenuItem value="Girls">Girls</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={qForm.is_visible}
                  onChange={(e) => setQForm((f) => ({ ...f, is_visible: Number(e.target.value) }))}
                >
                  <MenuItem value={1}>Published</MenuItem>
                  <MenuItem value={0}>Draft (hidden)</MenuItem>
                </Select>
              </FormControl>
            </Stack>}

            {qError && (
              <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1.5 }}>
                {qError}
              </Typography>
            )}

            {!qLoading && <>
              <Divider sx={{ my: 2.5 }} />

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={saveQuickEdit}
                  disabled={qSaving}
                  startIcon={qSaving ? <CircularProgress size={14} color="inherit" /> : null}
                  sx={{ flex: 1, textTransform: 'none' }}
                >
                  {qSaving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button variant="outlined" onClick={closeQuickEdit} disabled={qSaving} sx={{ textTransform: 'none' }}>
                  Cancel
                </Button>
              </Box>

              <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                <Button
                  size="small"
                  onClick={() => navigate(`/admin/products/edit/${quickEdit.id}`)}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.75rem' }}
                >
                  Open full editor →
                </Button>
              </Box>
            </>}
          </Box>
        )}
      </Drawer>
    </Card>
  );
}
